import { useEffect, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { Mic as MicIcon, Stop as StopIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { exercisesApi } from '@/api/exercises'
import { getErrorMessage } from '@/utils/error'
import type {
  ErrorCorrectionContent,
  OpenAnswerContent,
  OralAnswerContent,
  TranslationContent,
} from '../contentTypes'
import type { RendererProps } from './GapRenderers'

/**
 * Рендереры текстовых типов. Ответы: `{ clicked_index, correction }`,
 * `{ text }`, `{ audio_path }`.
 */

/** Тон вердикта автопроверки — те же три цвета, что и в разборе диктанта */
type VerdictTone = 'success' | 'warning' | 'error'

function toneColor(theme: Theme, tone: VerdictTone): string {
  if (tone === 'success') return theme.palette.success.main
  if (tone === 'warning') return theme.palette.warning.main

  return theme.palette.error.main
}

/**
 * Разбор автопроверки, годный к показу.
 *
 * Решающему ученику сервер `check_details` не отдаёт вовсе, поэтому пустой
 * разбор и незакрытая работа — одно и то же: вердикта нет, экран выглядит
 * ровно как до проверки.
 */
function checkDetails(
  check: Record<string, unknown> | null | undefined,
  readOnly: boolean | undefined,
): Record<string, unknown> | null {
  if (readOnly !== true || check === null || typeof check !== 'object') return null

  return Object.keys(check).length > 0 ? check : null
}

/** Булев флаг разбора: незнакомая форма — это отсутствие вердикта, а не ошибка */
function flag(details: Record<string, unknown> | null, key: string): boolean | null {
  const value = details?.[key]

  return typeof value === 'boolean' ? value : null
}

/**
 * Строка вердикта автопроверки.
 *
 * Цвет дублируется текстом: по одной заливке ученик с дальтонизмом не отличит
 * зачтённый ответ от незачтённого.
 */
function VerdictLine({ tone, text }: { tone: VerdictTone; text: string }) {
  return (
    <Typography
      variant="body2"
      sx={(theme) => ({
        px: 1,
        py: 0.5,
        borderRadius: 1,
        color: toneColor(theme, tone),
        bgcolor: alpha(toneColor(theme, tone), 0.14),
      })}
    >
      {text}
    </Typography>
  )
}

export function ErrorCorrectionRenderer({
  content,
  answers,
  onChange,
  readOnly,
  check,
}: RendererProps<ErrorCorrectionContent>) {
  const { t } = useTranslation()
  const clicked = typeof answers.clicked_index === 'number' ? (answers.clicked_index as number) : null

  // Автопроверка тут одна на всё задание: сервер сверяет клик с эталонным
  // токеном и, в режиме с исправлением, отдельно — текст самого исправления
  const details = checkDetails(check, readOnly)
  const clickOk = flag(details, 'click_ok')
  const fixOk = flag(details, 'fix_ok')

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('exercises.run.findError')}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {content.tokens.map((token, index) => {
          // Вердикт кладём только на тот токен, по которому ученик кликнул:
          // остальные сервер не оценивал, и красить их было бы выдумкой
          const judged = clickOk !== null && clicked === index

          return (
            <Chip
              key={index}
              label={token}
              color={clicked === index && !judged ? 'primary' : 'default'}
              variant={clicked === index ? 'filled' : 'outlined'}
              disabled={readOnly}
              onClick={() => onChange({ ...answers, clicked_index: index })}
              sx={
                judged
                  ? (theme) => {
                      const color = toneColor(theme, clickOk ? 'success' : 'error')

                      return {
                        color,
                        bgcolor: alpha(color, 0.14),
                        borderColor: color,
                        // На проверенной работе чипы отключены, а гашение до 38%
                        // съедает вместе с ними и сам вердикт
                        '&.Mui-disabled': { opacity: 1 },
                      }
                    }
                  : undefined
              }
            />
          )
        })}
      </Stack>

      {content.mode === 'click_and_fix' && clicked !== null && (
        <TextField
          size="small"
          label={t('exercises.run.correction')}
          value={typeof answers.correction === 'string' ? answers.correction : ''}
          disabled={readOnly}
          onChange={(event) => onChange({ ...answers, correction: event.target.value })}
        />
      )}

      {/* Словом вердикт нужен и тогда, когда подсвечивать нечего: не выбрав
          токен вовсе, ученик иначе увидел бы просто серые чипы */}
      {clickOk !== null && (
        <Stack spacing={1}>
          <VerdictLine
            tone={clickOk ? 'success' : 'error'}
            text={t(clickOk ? 'exercises.run.errorFound' : 'exercises.run.errorMissed')}
          />
          {fixOk !== null && (
            <VerdictLine
              tone={fixOk ? 'success' : 'error'}
              text={t(fixOk ? 'exercises.run.correctionOk' : 'exercises.run.correctionWrong')}
            />
          )}
        </Stack>
      )}
    </Stack>
  )
}

/**
 * Вердикт перевода.
 *
 * Совпал с эталоном — авто-100, всё остальное сервер отдаёт учителю: красный
 * там врал бы, вариантов перевода всегда больше, чем учитель успел выписать.
 */
function translationVerdict(details: Record<string, unknown> | null): { tone: VerdictTone; key: string } | null {
  if (details === null) return null
  if (details.matched === true) return { tone: 'success', key: 'exercises.run.translationMatched' }
  if (details.reason === 'empty') return { tone: 'warning', key: 'exercises.run.translationEmpty' }
  if (details.matched === false) return { tone: 'warning', key: 'exercises.run.translationForTeacher' }

  return null
}

export function TranslationRenderer({
  content,
  answers,
  onChange,
  readOnly,
  check,
}: RendererProps<TranslationContent>) {
  const { t } = useTranslation()

  // Поэлементной подсветки у перевода нет: сервер сравнивает ответ целиком,
  // поэтому показываем вердикт строкой и эталон, с которым он сравнивал
  const details = checkDetails(check, readOnly)
  const verdict = translationVerdict(details)
  const closest = typeof details?.closest === 'string' ? details.closest.trim() : ''

  return (
    <Stack spacing={2}>
      <Typography variant="h6">{content.source}</Typography>
      <Typography variant="caption" color="text.secondary">
        {t(content.direction === 'ru-en' ? 'exercises.run.translateToEn' : 'exercises.run.translateToRu')}
      </Typography>
      <TextField
        fullWidth
        multiline
        minRows={3}
        value={typeof answers.text === 'string' ? answers.text : ''}
        disabled={readOnly}
        onChange={(event) => onChange({ ...answers, text: event.target.value })}
      />

      {verdict && <VerdictLine tone={verdict.tone} text={t(verdict.key)} />}

      {verdict && closest !== '' && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('exercises.run.translationClosest')}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {closest}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

export function OpenAnswerRenderer({ content, answers, onChange, readOnly }: RendererProps<OpenAnswerContent>) {
  const { t } = useTranslation()
  const text = typeof answers.text === 'string' ? answers.text : ''
  const words = text.trim() === '' ? 0 : text.trim().split(/\s+/).length
  const minWords = content.min_words ?? null

  return (
    <Stack spacing={2}>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{content.prompt}</Typography>

      {content.image_url && (
        <Box component="img" src={content.image_url} alt="" sx={{ maxWidth: '100%', borderRadius: 1 }} />
      )}

      <TextField
        fullWidth
        multiline
        minRows={6}
        value={text}
        disabled={readOnly}
        onChange={(event) => onChange({ ...answers, text: event.target.value })}
        helperText={
          minWords === null
            ? t('exercises.run.wordCount', { count: words })
            : t('exercises.run.wordCountMin', { count: words, min: minWords })
        }
      />
    </Stack>
  )
}

/**
 * Устный ответ: запись микрофоном браузера.
 *
 * Формат не навязываем — Chrome отдаёт webm/opus, Safari m4a; сервер принимает
 * оба, и транскодировать на клиенте нечем. Файл ложится в хранилище УЧИТЕЛЯ,
 * поэтому переполнение квоты приходит ученику как ошибка загрузки.
 */
export function OralAnswerRenderer({
  content,
  answers,
  onChange,
  readOnly,
  runId,
}: RendererProps<OralAnswerContent> & { runId?: number }) {
  const { t } = useTranslation()
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const previewUrlRef = useRef<string | null>(null)
  const [recording, setRecording] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [seconds, setSeconds] = useState(0)

  const maxDuration = content.max_duration_sec ?? 120

  useEffect(() => {
    if (!recording) return

    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000)

    return () => window.clearInterval(timer)
  }, [recording])

  // Уход со страницы во время записи оставлял бы микрофон включённым, а каждая
  // перезапись — blob в памяти вкладки.
  useEffect(
    () => () => {
      recorderRef.current?.stream?.getTracks().forEach((track) => track.stop())
      recorderRef.current?.stop()
      recorderRef.current = null
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
    },
    [],
  )

  useEffect(() => {
    // Лимит длительности снимает запись сам: следить за ним ученику не нужно.
    // Останов идёт через ref, а не через замыкание на stop(): иначе эффект
    // пересоздавался бы на каждый рендер.
    if (recording && seconds >= maxDuration) {
      recorderRef.current?.stop()
      recorderRef.current = null
      setRecording(false)
    }
  }, [recording, seconds, maxDuration])

  const start = async () => {
    setError(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      chunksRef.current = []

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop())
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' })

        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
        const url = URL.createObjectURL(blob)
        previewUrlRef.current = url
        setPreviewUrl(url)

        if (runId === undefined) return

        setUploading(true)
        try {
          const extension = (recorder.mimeType || 'audio/webm').includes('mp4') ? 'm4a' : 'webm'
          const uploaded = await exercisesApi.uploadOralAnswer(runId, blob, `answer.${extension}`)
          onChange({ ...answers, audio_path: uploaded.path })
        } catch (err) {
          setError(getErrorMessage(err) || t('common.error'))
        } finally {
          setUploading(false)
        }
      }

      recorder.start()
      recorderRef.current = recorder
      setSeconds(0)
      setRecording(true)
    } catch {
      setError(t('exercises.run.microphoneDenied'))
    }
  }

  const stop = () => {
    recorderRef.current?.stop()
    recorderRef.current = null
    setRecording(false)
  }

  const savedPath = typeof answers.audio_path === 'string' ? (answers.audio_path as string) : null
  const savedUrl = typeof answers.audio_url === 'string' ? (answers.audio_url as string) : null

  return (
    <Stack spacing={2}>
      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{content.prompt}</Typography>

      {!readOnly && (
        <Stack direction="row" spacing={2} alignItems="center">
          {recording ? (
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={stop}>
              {t('exercises.run.stopRecording')}
            </Button>
          ) : (
            <Button variant="outlined" startIcon={<MicIcon />} disabled={uploading} onClick={() => void start()}>
              {savedPath ? t('exercises.run.recordAgain') : t('exercises.run.startRecording')}
            </Button>
          )}
          <Typography variant="body2" color="text.secondary">
            {recording
              ? t('exercises.run.recordingTime', { seconds, max: maxDuration })
              : t('exercises.run.maxDuration', { max: maxDuration })}
          </Typography>
        </Stack>
      )}

      {uploading && <Typography variant="body2">{t('exercises.run.uploading')}</Typography>}

      {/* Ссылка с сервера — чтобы запись слушал и учитель на проверке, а не
          только тот, кто её сейчас сделал */}
      {(previewUrl || savedUrl) && (
        <Box component="audio" src={previewUrl ?? savedUrl ?? undefined} controls sx={{ width: '100%' }} />
      )}
      {!previewUrl && !savedUrl && savedPath && (
        <Typography variant="body2">{t('exercises.run.answerRecorded')}</Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}
